import type { AuthorizationServer } from '@taler/api-client'
import { type Instant, parseInstant } from '@taler/domain'

export interface AppConfig {
  readonly apiBaseUrl: string
  readonly defaultLocale: 'en' | 'hu'
  readonly authorizationServer: AuthorizationServer
  /** Dev-only sign-in target. Stripped with the rest of that path in a production build. */
  readonly devSignInEmail: string | undefined
  /** BetterStack's Sentry-compatible ingestion endpoint. Unset disables error tracking. */
  readonly sentryDsn: string | undefined
}

const GOOGLE_AUTHORIZATION_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth'
const SCOPES = ['openid', 'email', 'profile']

export const CALLBACK_PATH = '/auth/callback'

export interface BuildInfo {
  /** Full SHA, or `unknown` when the build had neither a CI variable nor a git checkout. */
  readonly commitSha: string
  /** Undefined outside a Vite build (e.g. unit tests), never a placeholder instant. */
  readonly buildTime: Instant | undefined
}

export function readBuildInfo(env: Record<string, string | undefined> = import.meta.env): BuildInfo {
  const buildTime = env['VITE_APP_BUILD_TIME']
  return {
    commitSha: env['VITE_APP_COMMIT_SHA'] || 'unknown',
    buildTime: buildTime ? parseInstant(buildTime) : undefined,
  }
}

/** Same origin by default: the SPA and `/api/*` are served from one hostname. */
export function readConfig(
  env: Record<string, string | undefined> = import.meta.env,
  origin: string = globalThis.location?.origin ?? '',
): AppConfig {
  return {
    apiBaseUrl: env['VITE_TALER_API_BASE_URL'] ?? '',
    defaultLocale: env['VITE_TALER_LOCALE'] === 'hu' ? 'hu' : 'en',
    devSignInEmail: env['VITE_TALER_DEV_SIGN_IN_EMAIL'],
    sentryDsn: env['VITE_SENTRY_DSN'],
    authorizationServer: {
      authorizationEndpoint: env['VITE_GOOGLE_AUTHORIZATION_ENDPOINT'] ?? GOOGLE_AUTHORIZATION_ENDPOINT,
      // Public by design; the client secret lives on the backend, which is the confidential client.
      clientId: env['VITE_GOOGLE_CLIENT_ID'] ?? '',
      redirectUri: `${origin}${CALLBACK_PATH}`,
      scopes: SCOPES,
    },
  }
}
